// src/pages/SignIn.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
// import ReCAPTCHA from "react-google-recaptcha";
import { GoogleLogin } from '@react-oauth/google';
import { validateField } from "../utils/helpers/validate";
import NavBar from "../components/layout/NavBar";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import { request } from "../services/networking/request";
import { signin } from "../store/actions";
import TextInput from "../components/common/TextInput";
import Button from "../components/common/Button";
import OuterContainer from "../components/layout/OuterContainer";
import styled, { createGlobalStyle } from "styled-components";
import Meta from "../components/Meta";
import colors from "../utils/constants/colors";

/* =============================== Styling only =============================== */

const GlobalSpinStyles = createGlobalStyle`
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const AuthBg = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f9fc 0%, #f1f5fb 60%, #eef3fb 100%);
  padding-top: 64px; /* space for NavBar */
`;

const SignInContainer = styled.div`
  background: #ffffff;
  border: 1px solid #e9eef6;
  border-radius: 16px;
  margin: 48px auto;
  width: 460px;
  max-width: 95%;
  padding: 36px 32px;
  box-shadow: 0 12px 28px rgba(2, 8, 23, 0.06), 0 2px 8px rgba(2, 8, 23, 0.04);
`;

const Header = styled.h1`
  margin: 0 0 6px 0;
  text-align: center;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: 0.2px;
  color: #0f172a;
`;

const SubHeader = styled.p`
  margin: 0 0 24px 0;
  text-align: center;
  font-size: 14px;
  color: #64748b;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 18px;
  font-size: 14px;
  color: #475569;

  .link {
    color: ${colors.primary};
    font-weight: 700;
    cursor: pointer;
  }
`;

const ErrorMessage = styled.div`
  margin: 8px 0 12px;
  font-size: 13px;
  color: #b91c1c;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  padding: 8px 12px;
`;

const Divider = styled.div`
  height: 1px;
  background: #eef2f7;
  margin: 18px 0 8px;
`;

const RecaptchaWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
  /* Style the reCAPTCHA to match our design */
  .g-recaptcha {
    display: inline-block;
  }
`;

const GoogleSignInWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 20px 0;
  width: 100%;
  
  /* Style the Google Sign-In button container */
  > div {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    width: 100% !important;
    text-align: center !important;
  }
  
  /* Ensure the Google button iframe is centered */
  iframe {
    margin: 0 auto !important;
    display: block !important;
  }
  
  /* Target the Google button directly */
  [data-testid="google-signin-button"],
  .google-signin-button,
  div[role="button"] {
    margin: 0 auto !important;
    display: block !important;
  }
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  margin: 24px 0;
  color: #64748b;
  font-size: 14px;
  
  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e2e8f0;
  }
  
  &::before {
    margin-right: 16px;
  }
  
  &::after {
    margin-left: 16px;
  }
`;

/* =============================== Component =============================== */

const SignIn = (props) => {
  const navigate = useNavigate();
  const initialState = { email: "", password: "" };
  const [formValues, setFormValues] = useState(initialState);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState();
  // const [recaptchaToken, setRecaptchaToken] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((v) => ({ ...v, [name]: value }));
    setErrorMsg(""); // Clear error when user types
    const errors = validateField(name, value);
    setFormErrors((prev) => ({ ...prev, [name]: errors ? errors : null }));
  };

  useEffect(() => {
    if (isSubmit) {
      setIsSubmitting(true);
      props.signin({ type: "SIGNIN_INIT" });

      const endpoint = API_CONSTANT_MAP.signin;
      const result = request("POST", endpoint, {
        ...formValues,
        // recaptcha_token: recaptchaToken,
      });

      result
        .then((result) => {
          if ("token" in result.data) {
            props.signin({ type: "SIGNIN_SUCCESS", payload: result.data });
            
            // Check if user needs onboarding
            const onboardingStatus = result.data.user.onboarding_status;
            if (onboardingStatus === 'pending') {
              // User needs onboarding - redirect there with original redirect param
              const urlParams = new URLSearchParams(window.location.search);
              const redirectTo = urlParams.get('redirect');
              const onboardingUrl = redirectTo ? `/onboarding?redirect=${encodeURIComponent(redirectTo)}` : "/onboarding";
              navigate(onboardingUrl);
            } else {
              // User has completed or skipped onboarding - proceed normally
              const urlParams = new URLSearchParams(window.location.search);
              const redirectTo = urlParams.get('redirect');
              navigate(redirectTo || "/");
            }
          }
        })
        .catch((response) => {
          if (response.error) setErrorMsg(response.error.message);
          props.signin({ type: "SIGNIN_FAILURE" });
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  }, [isSubmit]); // eslint-disable-line

  const validate = (values) => {
    const errors = {};
    const email = validateField("email", values.email);
    const password = validateField("password", values.password);
    if (email) errors.email = email;
    if (password) errors.password = password;
    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validate(formValues);
    setFormErrors(errors);

    // if (!recaptchaToken) {
    //   setErrorMsg("Please complete the reCAPTCHA verification.");
    //   return;
    // }

    if (Object.keys(errors).length === 0) setIsSubmit(!isSubmit);
  };

  const handleCreateAccount = (e) => {
    e.preventDefault();
    navigate("/signup");
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    navigate("/password/reset/start");
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setIsSubmitting(true);
      props.signin({ type: "SIGNIN_INIT" });

      const endpoint = API_CONSTANT_MAP.google_signin;
      const result = await request("POST", endpoint, {
        googleToken: credentialResponse.credential,
      });

      if ("token" in result.data) {
        props.signin({ type: "SIGNIN_SUCCESS", payload: result.data });
        
        // Check for redirect parameter
        const urlParams = new URLSearchParams(window.location.search);
        const redirectTo = urlParams.get('redirect');
        navigate(redirectTo || "/");
      }
    } catch (response) {
      if (response.error) setErrorMsg(response.error.message);
      props.signin({ type: "SIGNIN_FAILURE" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleError = () => {
    setErrorMsg("Google Sign-In failed. Please try again.");
  };

  return (
    <Meta pageMeta={{ title: "Sign in - Doitly" }}>
      <GlobalSpinStyles />
      <NavBar user={props.state.user} />
      <AuthBg>
        <OuterContainer>
          <SignInContainer>
            <Header>Welcome to Doitly</Header>

            {/* Google Sign-In */}
            <GoogleSignInWrapper>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                theme="outline"
                size="large"
                text="signin_with"
                width="100%"
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              />
            </GoogleSignInWrapper>

            <OrDivider>Or continue with email</OrDivider>

            <form onSubmit={handleSubmit}>
              <TextInput
                value={formValues.email}
                name={"email"}
                label={"Email address"}
                onChange={handleChange}
                placeholder={"Enter your email"}
              />
              {formErrors.email && (
                <ErrorMessage>{formErrors.email}</ErrorMessage>
              )}

              <TextInput
                value={formValues.password}
                name={"password"}
                label={"Password"}
                onChange={handleChange}
                type={"password"}
                placeholder={"Enter your password"}
              />
              {formErrors.password && (
                <ErrorMessage>{formErrors.password}</ErrorMessage>
              )}

              {/* <RecaptchaWrapper>
                <ReCAPTCHA
                  sitekey={
                    process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
                    "YOUR_SITE_KEY_HERE"
                  }
                  onChange={(token) => {
                    setRecaptchaToken(token);
                    setErrorMsg(""); // Clear error when completed
                  }}
                  onExpired={() => setRecaptchaToken(null)}
                  onError={() => {
                    setRecaptchaToken(null);
                    setErrorMsg(
                      "reCAPTCHA verification failed. Please try again."
                    );
                  }}
                />
              </RecaptchaWrapper> */}

              <Button
                type="submit"
                disabled={isSubmitting}
                style={{
                  margin: "18px 0 10px",
                  background:
                    "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
                  borderRadius: "10px",
                  fontWeight: 800,
                  opacity: isSubmitting ? 0.7 : 1,
                }}
              >
                <span>
                  {isSubmitting && (
                    <span
                      style={{
                        display: "inline-block",
                        marginRight: "8px",
                        animation: "spin 1s linear infinite",
                      }}
                    >
                      ⟳
                    </span>
                  )}
                  {isSubmitting ? "Signing in..." : "Sign in"}
                </span>
              </Button>

              {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}

              <Divider />

              <Footer>
                <span>
                  New to Doitly?{" "}
                  <span className="link" onClick={handleCreateAccount}>
                    Sign up
                  </span>
                </span>
                <span className="link" onClick={handleForgotPassword}>
                  Forgot Password?
                </span>
              </Footer>
            </form>
          </SignInContainer>
        </OuterContainer>
      </AuthBg>
    </Meta>
  );
};

const mapStateToProps = (state) => ({ state });

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ signin }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(SignIn);
